import React, { useState } from 'react';
import { SelectDistrictType } from '../SelectDistrictType';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import Box from '@mui/material/Box';

interface AddDistrictTypeModalProps {
  stateName: string;
  buttonComponent?: React.ComponentType<{ onClick: () => void }>;
  onSelect: (districtType: string) => void;
  existingTypes?: string[]; // Optional: to filter out already selected types
  excludeTypes?: string[]; // Optional: to exclude specific types
  restrictToTiers?: number[]; // Optional: to restrict to specific tier numbers
  /** When set, only these district types are shown (e.g. measure-supporting types) */
  allowTypes?: string[];
}

export const AddDistrictTypeModal: React.FC<AddDistrictTypeModalProps> = ({
  stateName,
  buttonComponent: ButtonComponent,
  onSelect,
  existingTypes = [],
  excludeTypes = [],
  restrictToTiers,
  allowTypes
}) => {
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedType, setSelectedType] = useState<string | null>(null);

  const handleOpen = () => {
    setModalOpen(true);
    setSelectedType(null);
  };

  const handleClose = () => {
    setModalOpen(false);
    setSelectedType(null);
  };

  const handleSelect = () => {
    if (selectedType) {
      onSelect(selectedType);
      handleClose();
    }
  };

  return (
    <>
      {ButtonComponent ? (
        <ButtonComponent onClick={handleOpen} />
      ) : (
        <Button variant="outlined" onClick={handleOpen}>
          Add District Type
        </Button>
      )}
      <Dialog
        open={modalOpen}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">
              Add District Type
            </Typography>
            <IconButton onClick={handleClose} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ paddingTop: 2 }}>
            <Typography variant="body2" style={{ marginBottom: '12px' }}>
              Select a district type:
            </Typography>
            <SelectDistrictType
              stateName={stateName}
              value={selectedType}
              onChange={(type) => setSelectedType(type)}
              excludeTypes={[...existingTypes, ...excludeTypes]}
              restrictToTiers={restrictToTiers}
              allowTypes={allowTypes}
              placeholder="Select district type"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button
            onClick={handleSelect}
            variant="contained"
            color="primary"
            disabled={!selectedType}
          >
            Add
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
